import type { EventDto, MetricLatestResponse, Severity } from '@vision360/contracts';
import { lazy } from 'react';
import { z } from 'zod';
import { tagLabel } from '../_sdk/format';
import { RANGE_MS, toDataState, useEvents, useMetricLatest } from '../_sdk/hooks';
import { decimalsSchema, rangeSchema, unitSchema } from '../_sdk/schema';
import { defineWidget, type WidgetDataState } from '../_sdk/types';

/**
 * Part-of-whole (PRD VS-042): the newest value of every series of a metric,
 * summed per value of one tag, or a count of the latest 200 events by
 * severity or kind. A donut, or one stacked bar.
 */

export const EVENTS_LIMIT = 200;
/** Eight categorical slots: past eight, the smallest fold into « Autres ». */
export const MAX_SEGMENTS = 8;
export const OTHER = '__other';

export interface Segment {
  key: string;
  /** Data text (a tag value, a kind); severity and « Autres » are named by the render. */
  label: string;
  value: number;
  severity?: Severity;
}

export const PropsSchema = z
  .object({
    label: z.string().max(80).optional(),
    mode: z.enum(['metric', 'events']).default('events'),
    /** Metric: required. Events: empty = every source. */
    sourceId: z.uuid().optional(),
    metricName: z.string().min(1).max(200).optional(),
    /** The tag naming each segment; empty = every tag value, joined. */
    labelTag: z.string().max(64).default(''),
    unit: unitSchema,
    decimals: decimalsSchema,
    kindPrefix: z.string().max(200).optional(),
    groupBy: z.enum(['severity', 'kind']).default('severity'),
    range: rangeSchema.default('24h'),
    shape: z.enum(['donut', 'bar']).default('donut'),
  })
  .refine((p) => p.mode === 'events' || Boolean(p.sourceId && p.metricName));

export type DonutProps = z.output<typeof PropsSchema>;
export type DonutData = Segment[];

/** Largest first; past MAX_SEGMENTS the tail becomes one « Autres » segment. */
export function fold(segments: Segment[]): Segment[] {
  const sorted = [...segments].sort((a, b) => b.value - a.value);
  if (sorted.length <= MAX_SEGMENTS) return sorted;
  const kept = sorted.slice(0, MAX_SEGMENTS - 1);
  const rest = sorted.slice(MAX_SEGMENTS - 1).reduce((sum, s) => sum + s.value, 0);
  return [...kept, { key: OTHER, label: OTHER, value: rest }];
}

/** Sums per label: two series with the same tag value are one segment. Non-positive values have no share. */
export function metricSegments(items: MetricLatestResponse['items'], labelTag: string): Segment[] {
  const sums = new Map<string, number>();
  for (const item of items) {
    if (!(item.value > 0)) continue;
    const label = (labelTag && item.tags[labelTag]) || tagLabel(item.tags) || '—';
    sums.set(label, (sums.get(label) ?? 0) + item.value);
  }
  return fold([...sums].map(([label, value]) => ({ key: label, label, value })));
}

export function eventSegments(
  events: EventDto[],
  groupBy: DonutProps['groupBy'],
  since: number,
): Segment[] {
  const counts = new Map<string, { value: number; severity: Severity }>();
  for (const e of events) {
    if (Date.parse(e.ts) < since) continue;
    const key = groupBy === 'severity' ? String(e.severity) : e.kind;
    const entry = counts.get(key) ?? { value: 0, severity: e.severity };
    entry.value++;
    counts.set(key, entry);
  }
  if (groupBy === 'kind') {
    return fold([...counts].map(([key, { value }]) => ({ key, label: key, value })));
  }
  // Severity is an ordered state: most severe first, whatever the counts.
  return [...counts]
    .map(([key, { value, severity }]) => ({ key, label: key, value, severity }))
    .sort((a, b) => b.severity - a.severity);
}

const newest = (timestamps: string[]) => {
  const ms = Math.max(0, ...timestamps.map((ts) => Date.parse(ts)));
  return ms > 0 ? new Date(ms) : undefined;
};

function useMetricSegments(p: DonutProps): WidgetDataState<DonutData> {
  const query = useMetricLatest(p.sourceId ?? '', p.metricName ?? '', {});
  return toDataState(query, {
    select: (r) => metricSegments(r.items, p.labelTag),
    isEmpty: (segments) => segments.length === 0,
    lastUpdated: () => newest(query.data?.items.map((i) => i.ts) ?? []),
  });
}

function useEventSegments(p: DonutProps): WidgetDataState<DonutData> {
  const query = useEvents({
    sourceId: p.sourceId || undefined,
    kindPrefix: p.kindPrefix || undefined,
    limit: EVENTS_LIMIT,
  });
  return toDataState(query, {
    select: (r) => eventSegments(r.events, p.groupBy, Date.now() - RANGE_MS[p.range]),
    isEmpty: (segments) => segments.length === 0,
    lastUpdated: () => newest(query.data?.events.map((e) => e.ts) ?? []),
  });
}

export default defineWidget<DonutProps, DonutData>({
  type: 'donut',
  category: 'metric',
  defaultSize: { w: 3, h: 3 },
  minSize: { w: 2, h: 2 },
  propsSchema: PropsSchema,
  defaults: {
    mode: 'events',
    groupBy: 'severity',
    range: '24h',
    labelTag: '',
    decimals: 0,
    shape: 'donut',
  },
  sourceIds: (p) => (p.sourceId ? [p.sourceId] : []),
  // One hook per mode. The frame remounts an instance whenever its props change
  // (its error boundary is keyed by them), so an instance never switches hooks.
  useData: (p) => (p.mode === 'events' ? useEventSegments : useMetricSegments)(p),
  Render: lazy(() => import('./render')),
  Editor: lazy(() => import('./editor')),
});
